import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
const IMPERSONATION_QUERY_KEY = ['impersonationState'];
/**
 * Checks if a user with the given role is allowed to impersonate others.
 * - system: can impersonate
 * - admin: can impersonate
 * - supervisor/user: cannot impersonate
 */
export function canRoleImpersonate(role) {
    return role === 'system' || role === 'admin';
}
/**
 * Returns the list of roles that a given role is allowed to impersonate.
 * - system: can impersonate admin, supervisor, user (NOT other system)
 * - admin: can impersonate supervisor, user (NOT admin, NOT system)
 */
export function getImpersonatableRoles(currentRole) {
    if (currentRole === 'system')
        return ['admin', 'supervisor', 'user'];
    if (currentRole === 'admin')
        return ['supervisor', 'user'];
    return [];
}
/**
 * Hook to manage user impersonation using React Query for state management.
 *
 * Stores the impersonated userId in the query cache, which acts as a global store.
 * This works in React Native environments where window.dispatchEvent is not available.
 */
export const useImpersonation = () => {
    const queryClient = useQueryClient();
    // Read state from Query Cache
    const { data: impersonatedUserId } = useQuery({
        queryKey: IMPERSONATION_QUERY_KEY,
        queryFn: () => null, // Initial value / no-op fetch
        initialData: null,
        staleTime: Infinity, // Never refetch, acts as client-state
        gcTime: Infinity, // Keep in cache indefinitely
    });
    const resetUserContext = useCallback(() => {
        // Invalidate queries but exclude impersonation state to prevent resetting it
        queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] !== IMPERSONATION_QUERY_KEY[0]
        });
    }, [queryClient]);
    const startImpersonation = useCallback((userId) => {
        // Update Query Cache
        queryClient.setQueryData(IMPERSONATION_QUERY_KEY, userId);
        resetUserContext();
    }, [queryClient, resetUserContext]);
    const stopImpersonation = useCallback(() => {
        // Update Query Cache
        queryClient.setQueryData(IMPERSONATION_QUERY_KEY, null);
        resetUserContext();
    }, [queryClient, resetUserContext]);
    const isImpersonating = !!impersonatedUserId;
    return {
        impersonatedUserId: impersonatedUserId,
        isImpersonating,
        startImpersonation,
        stopImpersonation,
    };
};
//# sourceMappingURL=useImpersonation.js.map